import { useState } from 'react';

interface PasswordFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  hint?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  variant?: 'login' | 'signup';
}

export const PasswordField = ({
  id = 'password',
  label,
  value,
  onChange,
  placeholder = '••••••••',
  required = false,
  minLength,
  hint,
  onKeyDown,
  variant = 'login',
}: PasswordFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);

  if (variant === 'signup') {
    return (
      <div className="flex flex-col gap-2">
        <label className="text-[#101822] text-sm font-semibold ml-1" htmlFor={id}>
          {label}
        </label>
        <div className="flex w-full items-center rounded-xl border-2 border-[#101822]/5 bg-white focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/5 transition-all overflow-hidden">
          <div className="pl-4 flex items-center justify-center text-[#101822]/40 shrink-0">
            <span className="material-symbols-outlined text-xl">lock</span>
          </div>
          <input
            className="flex-1 w-full bg-transparent border-none text-[#101822] placeholder-[#101822]/20 focus:ring-0 text-base py-3 pl-3 pr-2 outline-none"
            id={id}
            placeholder={placeholder}
            type={showPassword ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            required={required}
            minLength={minLength}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="pr-4 flex items-center justify-center text-[#101822]/40 hover:text-primary transition-colors bg-transparent border-none focus:outline-none shrink-0"
          >
            <span className="material-symbols-outlined text-xl">
              {showPassword ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        </div>
        {hint && <p className="text-[#101822]/40 text-xs ml-1">{hint}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[#101822] text-sm font-semibold leading-normal ml-1" htmlFor={id}>
        {label}
      </label>
      <div className="flex w-full items-center rounded-xl border border-[#dbe0e6] bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/30 transition-all h-12 overflow-hidden">
        <div className="pl-4 flex items-center justify-center text-slate-400">
          <span className="material-symbols-outlined text-[20px]">lock</span>
        </div>
        <input
          className="flex-1 w-full bg-transparent border-none text-[#101822] placeholder:text-slate-400 focus:ring-0 text-base h-full py-0 pl-3 pr-4"
          id={id}
          placeholder={placeholder}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          required={required}
          minLength={minLength}
        />
        <button
          className="pr-4 flex items-center justify-center text-slate-400 hover:text-primary cursor-pointer transition-colors bg-transparent focus:outline-none focus:ring-0 hover:border-none border-none"
          type="button"
          onClick={() => setShowPassword(!showPassword)}
        >
          <span className="material-symbols-outlined text-[20px]">
            {showPassword ? 'visibility_off' : 'visibility'}
          </span>
        </button>
      </div>
      {hint && <p className="text-slate-400 text-xs ml-1">{hint}</p>}
    </div>
  );
};
